import React from "react";
import Dropdown from "../../../../Components/Atoms/Dropdown";
import { ReactComponent as KebabMenuIcon } from "../../../../Assets/ActionIcons/KebabMenuIcon.svg";
import { useAuth0 } from "@auth0/auth0-react";
import { useCustomerStore } from "../../../../Store/Customer";
import { Customer } from "../../../../Contracts/Customer";
import { DataSourceItem } from "../../../../Components/Atoms/Table";
import Toggle from "../../../../Components/Atoms/Toggle";
import { useBannerStore } from "../../../../Store/Banner";
import { BannerType } from "../../../../Contracts/Banner";

interface Props {
  row: DataSourceItem<Customer>;
  open: boolean;
  onOpen: () => void;
  onClose: () => void;
  refreshList: () => void;
}

const IntegratedCustomerActions: React.FC<Props> = ({
  row,
  open,
  onOpen,
  onClose,
  refreshList
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const {
    editCustomerDetails,
    requestCustomerToAddPaymentMethod,
    unmapGrowCustomer,
    customersCount
  } = useCustomerStore();
  const { addBanner } = useBannerStore();

  const cid = row.data.id.toString();
  const invoiceEnabled = Boolean(row.data.reminder_enabled_date);
  const autoCollectEnabled = Boolean(row.data.auto_collection_enabled_date);

  const handleRequestPaymentMethod = async () => {
    const total = customersCount || 0;
    if (
      !window.confirm(
        `Are you sure you want to send email ${
          total
        } selected clients for adding authorization or mandate?`
      )
    ) {
      return;
    }
    try {
      await requestCustomerToAddPaymentMethod(getAccessTokenSilently, cid);
      addBanner({
        type: BannerType.Success,
        message: `Sent payment method request email successfully to ${row.data.name}.`
      });
    } catch (err: any) {
      window.alert(err?.message || String(err));
    }
    onClose();
  };

  const handleToggleInvoiceEmails = async (checked: boolean) => {
    try {
      const payload = {
        reminder_enabled_date: checked ? new Date() : null,
        ...(!checked && autoCollectEnabled ? { auto_collection_enabled_date: null } : {})
      };

      await editCustomerDetails(getAccessTokenSilently, cid, payload);
      addBanner({
        type: BannerType.Success,
        message: `Invoice emails ${checked ? "enabled" : "disabled"} for ${row.data.name} successfully.`
      });
    } catch (err: any) {
      window.alert(err?.message || String(err));
    }
    onClose();
  };

  const handleToggleAutoCollect = async (checked: boolean) => {
    try {
      if (checked) {
        await editCustomerDetails(getAccessTokenSilently, cid, {
          reminder_enabled_date: new Date(),
          auto_collection_enabled_date: new Date()
        });
      } else {
        await editCustomerDetails(getAccessTokenSilently, cid, {
          auto_collection_enabled_date: null
        });
      }
      addBanner({
        type: BannerType.Success,
        message: `Auto collect ${checked ? "enabled" : "disabled"} for ${row.data.name} successfully.`
      });
    } catch (err: any) {
      window.alert(err?.message || String(err));
    }
    onClose();
  };

  const handleUnmapClient = async () => {
    if (!window.confirm(`Are you sure you want to unmap the client ${row.data.name}?`)) {
      return;
    }
    try {
      if (row.data.payments_grow_customer_id) {
        await unmapGrowCustomer(getAccessTokenSilently, row.data.payments_grow_customer_id);
        refreshList();
        addBanner({
          type: BannerType.Success,
          message: `Unmapped client ${row.data.name} successfully.`
        });
      }
    } catch (err: any) {
      window.alert(err?.message || String(err));
    }
    onClose();
  };

  const options = [
    { label: "Request to add payment method", value: "request_to_add_payment_method" },
    {
      label: (
        <div className="flex space-x-2 items-center justify-between w-60 px-4 py-2">
          <span>Invoice Emails</span>
          <Toggle
            checked={invoiceEnabled}
            onChange={handleToggleInvoiceEmails}
            className="scale-75"
          />
        </div>
      ),
      disabled: true,
      value: "toggle_invoice_emails"
    },
    {
      label: (
        <div className="flex space-x-2 items-center justify-between w-60 px-4 py-2">
          <span>Auto Collect</span>
          <Toggle
            checked={autoCollectEnabled}
            onChange={handleToggleAutoCollect}
            className="scale-75"
          />
        </div>
      ),
      disabled: true,
      value: "toggle_invoice_emails_and_auto_collect"
    },
    ...(row.data.payments_grow_customer_id
      ? [
          {
            label: "Unmap Client",
            value: "unmap_client",
            className: "text-red-600 border-t border-gray-200"
          }
        ]
      : [])
  ];

  return (
    <div onClick={() => (open ? onClose() : onOpen())}>
      <Dropdown
        options={options}
        position="bottom-right"
        onChange={value => {
          if (value === "request_to_add_payment_method") {
            handleRequestPaymentMethod();
          } else if (value === "unmap_client") {
            handleUnmapClient();
          }
        }}
        anchor={
          <div className="cursor-pointer flex flex-col items-center">
            <KebabMenuIcon className={`${open ? "text-blue-400" : "text-blue-600"}`} />
            <div className={`${open ? "text-blue-400" : "text-blue-600"}`}>More</div>
          </div>
        }
      />
    </div>
  );
};

export default IntegratedCustomerActions;
